import { StatusBar } from 'expo-status-bar';
import { Suspense, lazy, useEffect } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { BrowserRouter, MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './lib/contexts/AuthContext';
import { ThemeProvider } from './lib/contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { UserAppMobileGate } from './components/user-app/UserAppMobileGate';
import { ComensalRegistroPage } from './components/user-app/ComensalRegistroPage';
import { ComensalRegistroEmailRedirectPage } from './components/user-app/ComensalRegistroEmailRedirectPage';
import { ComensalCorreoVerificadoPage } from './components/user-app/ComensalCorreoVerificadoPage';
import { ComensalAppRoute } from './components/user-app/ComensalAppRoute';
import { ComensalAppHome } from './components/user-app/ComensalAppHome';
import { ComensalLikesPage } from './components/user-app/ComensalLikesPage';
import { ComensalRutaPage } from './components/user-app/ComensalRutaPage';
import { ComensalPerfilPage } from './components/user-app/ComensalPerfilPage';
import { ComensalUbicacionRequeridaPage } from './components/user-app/ComensalUbicacionRequeridaPage';
import { ComensalLoginPage } from './components/user-app/ComensalLoginPage';
import { ComensalRecuperarContrasenaPage } from './components/user-app/ComensalRecuperarContrasenaPage';
import { ComensalRestaurantesCercanosPage } from './components/user-app/ComensalRestaurantesCercanosPage';
import { ComensalVotarPage } from './components/user-app/ComensalVotarPage';
import { ComensalRestaurantePorQrPage } from './components/user-app/ComensalRestaurantePorQrPage';
import { RestaurantePublicQrPage } from './components/RestaurantePublicQrPage';
import { VotacionWebGate } from './components/VotacionWebGate';
import { WebAppUpdatePrompt } from './components/WebAppUpdatePrompt';
import { PwaStaffDashboardRedirect } from './components/PwaStaffDashboardRedirect';
import { HostRoutingGate } from './components/HostRoutingGate';
import { isDashboardHost } from './lib/constants/hostRouting';
import { registerPwa } from './lib/registerPwa';

const LandingPage = lazy(() =>
  import('./components/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const Login = lazy(() => import('./components/Login').then((m) => ({ default: m.Login })));
const Dashboard = lazy(() =>
  import('./components/Dashboard').then((m) => ({ default: m.Dashboard }))
);
const Design = lazy(() =>
  import('./components/Design').then((m) => ({ default: m.Design }))
);
const PantallaVotacion = lazy(() =>
  import('./components/PantallaVotacion').then((m) => ({ default: m.PantallaVotacion }))
);
const PropuestasPage = lazy(() =>
  import('./components/PropuestasPage').then((m) => ({ default: m.PropuestasPage }))
);
const GanadoresCategoriaPage = lazy(() =>
  import('./components/GanadoresCategoriaPage').then((m) => ({
    default: m.GanadoresCategoriaPage,
  }))
);
const NotasPrensaPage = lazy(() =>
  import('./components/NotasPrensaPage').then((m) => ({ default: m.NotasPrensaPage }))
);
const ContactoPage = lazy(() =>
  import('./components/ContactoPage').then((m) => ({ default: m.ContactoPage }))
);
const DescargasPage = lazy(() =>
  import('./components/DescargasPage').then((m) => ({ default: m.DescargasPage }))
);
const PrivacidadPage = lazy(() =>
  import('./components/PrivacidadPage').then((m) => ({ default: m.PrivacidadPage }))
);
const EliminarCuentaPage = lazy(() =>
  import('./components/EliminarCuentaPage').then((m) => ({ default: m.EliminarCuentaPage }))
);
const CdashPage = lazy(() =>
  import('./components/cdash/CdashPage').then((m) => ({ default: m.CdashPage }))
);
const RouterComponent = Platform.OS === 'web' ? BrowserRouter : MemoryRouter;

function RoutesLoadingFallback() {
  return (
    <View
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F5F6F8',
      }}
    >
      <ActivityIndicator size="large" color="#A20000" />
    </View>
  );
}

function WebRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/ediciones" element={<LandingPage />} />
      <Route path="/ganadores" element={<LandingPage />} />
      <Route path="/ganadores/:categoriaEnc" element={<GanadoresCategoriaPage />} />
      <Route path="/notas-de-prensa" element={<NotasPrensaPage />} />
      <Route path="/contacto" element={<ContactoPage />} />
      {/* URL canónica de marketing. /descargar redirige por QRs ya impresos. */}
      <Route path="/app" element={<DescargasPage />} />
      <Route path="/descargar" element={<Navigate to="/app" replace />} />
      <Route path="/descargas" element={<Navigate to="/app" replace />} />
      <Route path="/privacidad" element={<PrivacidadPage />} />
      <Route path="/eliminar-cuenta" element={<EliminarCuentaPage />} />
      {/* Confirmación de correo (enlaces viejos /app/registro/confirmar también caen en /app/*). */}
      <Route path="/registro/confirmar" element={<Navigate to="/comensal/correo-verificado" replace />} />
      <Route path="/comensal/correo-verificado" element={<ComensalCorreoVerificadoPage />} />
      <Route
        path="/r/:codigoQr"
        element={
          <VotacionWebGate>
            <RestaurantePublicQrPage />
          </VotacionWebGate>
        }
      />
      <Route path="/cdash" element={<CdashPage />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/design"
        element={
          <ProtectedRoute requiredRole="owner">
            <Design />
          </ProtectedRoute>
        }
      />
      <Route
        path="/votar/:id"
        element={
          <VotacionWebGate>
            <PantallaVotacion />
          </VotacionWebGate>
        }
      />
      <Route path="/edicion/:id" element={<PropuestasPage />} />
      {/* Rutas viejas de shell web /app/... → landing de descarga */}
      <Route path="/app/*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

/** App instalada (Expo): sin prefijo /app; esa URL queda libre para marketing en web. */
function NativeRoutes() {
  return (
    <Routes>
      <Route
        path="/registro/confirmar"
        element={
          <UserAppMobileGate>
            <ComensalRegistroEmailRedirectPage />
          </UserAppMobileGate>
        }
      />
      <Route
        path="/registro"
        element={
          <UserAppMobileGate>
            <ComensalRegistroPage />
          </UserAppMobileGate>
        }
      />
      <Route
        path="/entrar"
        element={
          <UserAppMobileGate>
            <ComensalLoginPage />
          </UserAppMobileGate>
        }
      />
      <Route
        path="/recuperar-contrasena"
        element={
          <UserAppMobileGate>
            <ComensalRecuperarContrasenaPage />
          </UserAppMobileGate>
        }
      />
      <Route
        path="/ubicacion-requerida"
        element={
          <UserAppMobileGate>
            <ComensalUbicacionRequeridaPage />
          </UserAppMobileGate>
        }
      />
      <Route
        path="/"
        element={
          <UserAppMobileGate>
            <ComensalAppRoute />
          </UserAppMobileGate>
        }
      >
        <Route index element={<ComensalAppHome />} />
        <Route path="likes" element={<ComensalLikesPage />} />
        <Route path="ruta" element={<ComensalRutaPage />} />
        <Route path="perfil" element={<ComensalPerfilPage />} />
        <Route path="restaurantes-cercanos" element={<ComensalRestaurantesCercanosPage />} />
        <Route path="restaurante/:codigoQr" element={<ComensalRestaurantePorQrPage />} />
        <Route path="votar" element={<ComensalVotarPage />} />
        <Route path="edicion/:id" element={<PropuestasPage />} />
      </Route>
      <Route path="/votar/:id" element={<PantallaVotacion />} />
      <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/design"
        element={
          <ProtectedRoute requiredRole="owner">
            <Design />
          </ProtectedRoute>
        }
      />
      <Route path="/privacidad" element={<PrivacidadPage />} />
      <Route path="/eliminar-cuenta" element={<EliminarCuentaPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    try {
      console.log('🚀 App: Inicializando aplicación...');

      const hostname =
        Platform.OS === 'web' &&
        typeof window !== 'undefined' &&
        window.location != null
          ? window.location.hostname
          : '';
      let title = 'As Gastronómico App';
      const isStaging =
        hostname.includes('d1vqfq5zg') || hostname.includes('staging') || hostname.includes('preview');
      const isDevelopment =
        hostname.includes('localhost') || hostname.includes('192.168.0.101');
      if (isDashboardHost(hostname)) title = 'Dashboard As Gastronómico';
      else if (isStaging) title = '(staging) As Gastronómico App';
      else if (isDevelopment) title = '(dev) As Gastronómico App';

      if (typeof document !== 'undefined') {
        document.title = title;
        const titleElement = document.querySelector('title');
        if (titleElement) titleElement.textContent = title;
        const htmlElement = document.documentElement;
        if (htmlElement) {
          htmlElement.setAttribute('lang', 'es');
          htmlElement.setAttribute('translate', 'no');
        }
        let metaTranslate = document.querySelector('meta[name="google"]');
        if (!metaTranslate) {
          metaTranslate = document.createElement('meta');
          metaTranslate.setAttribute('name', 'google');
          document.head.appendChild(metaTranslate);
        }
        metaTranslate.setAttribute('content', 'notranslate');
        const bodyElement = document.body;
        if (bodyElement) {
          bodyElement.setAttribute('translate', 'no');
          bodyElement.setAttribute('data-translate', 'no');
        }
      }

      registerPwa();
      console.log('✅ App: Inicialización completada');
    } catch (error) {
      console.error('❌ App: Error en inicialización', error);
    }
  }, []);

  return (
    <ErrorBoundary>
      <SafeAreaProvider>
        <ThemeProvider>
        <AuthProvider>
          <RouterComponent
            {...(Platform.OS === 'web' ? {} : { initialEntries: ['/'] })}
          >
            <HostRoutingGate>
              <Suspense fallback={<RoutesLoadingFallback />}>
                {Platform.OS === 'web' ? <WebRoutes /> : <NativeRoutes />}
              </Suspense>
              {Platform.OS === 'web' ? <PwaStaffDashboardRedirect /> : null}
              {Platform.OS === 'web' ? <WebAppUpdatePrompt /> : null}
            </HostRoutingGate>
            <StatusBar style="auto" />
          </RouterComponent>
        </AuthProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </ErrorBoundary>
  );
}
